import React, { useEffect, useState } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import {
  Activity,
  CalendarDays,
  Check,
  ChevronDown,
  CircleAlert,
  CircleDot,
  Clock3,
  FileCheck2,
  MapPin,
  Play,
  ShieldCheck,
} from 'lucide-react';

const operationalSteps = [
  { label: 'Attract', detail: 'New request received', status: 'Done', tone: 'done' },
  { label: 'Schedule', detail: 'Visit confirmed', status: 'Done', tone: 'done' },
  { label: 'Prepare', detail: 'Intake received', status: 'Done', tone: 'done' },
  { label: 'Treat', detail: 'Clinical queue ready', status: 'Ready', tone: 'ready' },
  { label: 'Collect', detail: 'Coverage in review', status: 'Needs review', tone: 'review' },
  { label: 'Retain', detail: 'Follow-up planned', status: 'Running', tone: 'running' },
];

const workStates = [
  {
    label: 'Queued',
    tone: 'ready',
    title: 'Upcoming visit · Readiness work queued',
    reasonLabel: 'Why this work is queued',
    reason: 'The visit entered its preparation window.',
    detail: 'Autopilot attached the chart, coverage, messages, and location rules.',
  },
  {
    label: 'In progress',
    tone: 'running',
    title: 'Upcoming visit · Readiness checks in progress',
    reasonLabel: 'What Autopilot is checking',
    reason: 'The visit record needs a current coverage response.',
    detail: 'Autopilot is checking the available source context within office policy.',
  },
  {
    label: 'Needs review',
    tone: 'review',
    title: 'Upcoming visit · Ready for office review',
    reasonLabel: 'Why this needs review',
    reason: 'Coverage response conflicts with the visit plan.',
    detail: 'Autopilot gathered the source context and prepared the next safe action.',
  },
  {
    label: 'Completed',
    tone: 'done',
    title: 'Upcoming visit · Readiness work completed',
    reasonLabel: 'What was completed',
    reason: 'The visit record and office queue are current.',
    detail: 'Autopilot recorded the result and made the next obligation visible.',
  },
];

const activityRows = [
  {
    text: 'Schedule gap detected → outreach started → appointment booked',
    status: 'Completed',
    tone: 'done',
    Icon: Check,
  },
  {
    text: 'Eligibility incomplete → verification started → chart updated',
    status: 'In progress',
    tone: 'running',
    Icon: Play,
  },
  {
    text: 'Claim returned → documentation checked → resubmitted',
    status: 'Needs review',
    tone: 'review',
    Icon: CircleAlert,
  },
  {
    text: 'Patient response received → next step completed',
    status: 'Completed',
    tone: 'done',
    Icon: Check,
  },
];

const autonomyControls = [
  { label: 'Complete automatically', meta: 'Within policy', active: true },
  { label: 'Require approval', meta: 'Protected actions' },
  { label: 'Escalate with context', meta: 'Exceptions' },
];

const Status = ({ tone, children }) => (
  <span className={`alt4-command-status alt4-command-status--${tone}`}>
    <span className="alt4-command-status__mark" aria-hidden="true" />
    {children}
  </span>
);

export default function PracticeCommandCenter({ compact = false }) {
  const reduceMotion = useReducedMotion();
  const [activeWorkIndex, setActiveWorkIndex] = useState(0);
  const visibleWorkIndex = reduceMotion ? workStates.length - 1 : activeWorkIndex;
  const currentWork = workStates[visibleWorkIndex];

  useEffect(() => {
    if (reduceMotion || activeWorkIndex >= workStates.length - 1) return undefined;

    const timer = window.setTimeout(() => {
      setActiveWorkIndex((index) => Math.min(index + 1, workStates.length - 1));
    }, 2800);

    return () => window.clearTimeout(timer);
  }, [activeWorkIndex, reduceMotion]);

  return (
    <figure
      className={`alt4-command-center${compact ? ' alt4-command-center--compact' : ''}`}
      aria-labelledby="alt4-command-center-caption"
    >
      <figcaption id="alt4-command-center-caption" className="alt4-sr-only">
        Practice Command Center showing Autopilot observing, deciding, acting, and verifying work
        across the patient lifecycle, with approvals and a complete activity trail.
      </figcaption>

      <div className="alt4-command-center__chrome" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>

      <header className="alt4-command-center__header">
        <div>
          <p className="alt4-command-center__kicker">Practice Command Center</p>
          <h2>All locations · Operating now</h2>
        </div>
        <div className="alt4-command-center__header-controls">
          <div className="alt4-command-center__location" aria-label="Current view: all locations">
            <MapPin size={14} aria-hidden="true" />
            <span>All locations</span>
            <ChevronDown size={13} aria-hidden="true" />
          </div>
          <div className="alt4-autopilot-state">
            <motion.span
              className="alt4-autopilot-state__pulse"
              aria-hidden="true"
              animate={reduceMotion ? undefined : { opacity: [0.45, 1, 0.45], scale: [0.9, 1, 0.9] }}
              transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
            />
            Autopilot on
          </div>
        </div>
      </header>

      <div className="alt4-command-center__loop" aria-label="Autopilot operating loop">
        {['Observe', 'Decide', 'Act', 'Verify'].map((step, index) => (
          <React.Fragment key={step}>
            <span className={index === 2 ? 'is-active' : ''}>
              {index === 0 && <Activity size={13} aria-hidden="true" />}
              {index === 1 && <CircleDot size={13} aria-hidden="true" />}
              {index === 2 && <Play size={12} aria-hidden="true" />}
              {index === 3 && <ShieldCheck size={13} aria-hidden="true" />}
              {step}
            </span>
            {index < 3 && <i aria-hidden="true">→</i>}
          </React.Fragment>
        ))}
        <motion.div
          className="alt4-command-center__loop-progress"
          aria-hidden="true"
          animate={reduceMotion ? undefined : { scaleX: [0.12, 1], opacity: [0.5, 1] }}
          transition={{ duration: 3.8, repeat: Infinity, repeatDelay: 0.7, ease: 'easeInOut' }}
        />
      </div>

      <div className="alt4-command-center__body">
        <aside className="alt4-command-center__rail" aria-label="Patient lifecycle">
          <div className="alt4-command-center__rail-heading">
            <div>
              <span>Today</span>
              <strong>Office flow</strong>
            </div>
            <CalendarDays size={16} aria-hidden="true" />
          </div>

          <ol className="alt4-command-center__steps">
            {operationalSteps.map((step, index) => (
              <li key={step.label} className={step.tone === 'review' ? 'is-current' : ''}>
                <div className="alt4-command-center__step-track" aria-hidden="true">
                  <span>{index + 1}</span>
                  {index < operationalSteps.length - 1 && <i />}
                </div>
                <div className="alt4-command-center__step-copy">
                  <strong>{step.label}</strong>
                  <small>{step.detail}</small>
                </div>
                <Status tone={step.tone}>{step.status}</Status>
              </li>
            ))}
          </ol>
        </aside>

        <section className="alt4-command-center__work" aria-label="Current office work">
          <div className="alt4-command-center__work-heading">
            <motion.div
              key={currentWork.label}
              initial={reduceMotion ? undefined : { opacity: 0.7, y: 3 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.45, ease: 'easeOut' }}
            >
              <span className="alt4-command-center__section-label">Current work item</span>
              <h3>{currentWork.title}</h3>
            </motion.div>
            <span data-testid="command-work-status" aria-live="polite">
              <Status tone={currentWork.tone}>{currentWork.label}</Status>
            </span>
          </div>

          <div className="alt4-command-center__context">
            <div>
              <Clock3 size={15} aria-hidden="true" />
              <span>
                <small>Visit window</small>
                <strong>Tomorrow morning</strong>
              </span>
            </div>
            <div>
              <FileCheck2 size={15} aria-hidden="true" />
              <span>
                <small>Context gathered</small>
                <strong>Chart + coverage + messages</strong>
              </span>
            </div>
          </div>

          <div className="alt4-command-center__decision">
            <div className="alt4-command-center__decision-icon" aria-hidden="true">
              <ShieldCheck size={17} />
            </div>
            <div>
              <span>{currentWork.reasonLabel}</span>
              <strong>{currentWork.reason}</strong>
              <p>{currentWork.detail}</p>
            </div>
          </div>

          <div className="alt4-command-center__controls">
            <span className="alt4-command-center__section-label">Human control</span>
            {autonomyControls.map((control) => (
              <div
                key={control.label}
                className={`alt4-command-center__control${control.active ? ' is-active' : ''}`}
              >
                <span className="alt4-command-center__radio" aria-hidden="true">
                  {control.active && <i />}
                </span>
                <strong>{control.label}</strong>
                <small>{control.meta}</small>
              </div>
            ))}
          </div>
        </section>

        <aside className="alt4-command-center__activity" aria-label="Activity trail">
          <div className="alt4-command-center__activity-heading">
            <div>
              <span className="alt4-command-center__section-label">Activity trail</span>
              <strong>What the system did—and why</strong>
            </div>
            <Activity size={16} aria-hidden="true" />
          </div>

          <ol>
            {activityRows.map(({ text, status, tone, Icon }, index) => (
              <motion.li
                key={text}
                animate={
                  reduceMotion
                    ? undefined
                    : { borderColor: ['rgba(21,87,255,0.1)', 'rgba(21,87,255,0.35)', 'rgba(21,87,255,0.1)'] }
                }
                transition={{ duration: 3.2, delay: index * 0.65, repeat: Infinity, repeatDelay: 2 }}
              >
                <span className={`alt4-command-center__activity-icon is-${tone}`} aria-hidden="true">
                  <Icon size={13} />
                </span>
                <div>
                  <p>{text}</p>
                  <Status tone={tone}>{status}</Status>
                </div>
              </motion.li>
            ))}
          </ol>
        </aside>
      </div>

      <footer className="alt4-command-center__legend" aria-label="Status legend">
        <span>Status</span>
        <Status tone="ready">Queued</Status>
        <Status tone="running">In progress</Status>
        <Status tone="review">Needs review</Status>
        <Status tone="done">Completed</Status>
        <span className="alt4-command-center__legend-note">
          <ShieldCheck size={13} aria-hidden="true" />
          Every action follows your controls
        </span>
      </footer>
    </figure>
  );
}
